import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Form, Checkbox, Flex } from "antd"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { Modal } from "ui/Modal"

import { Text } from "ui/Typography"
import DataTable from "ui/DataTable"
import useScheduleTimeOffForm from "./hooks/useScheduleTimeOffForm"
import useScheduledTimeOffColumns from "./hooks/useScheduledTimeOffColumns"
import DateRangeFields from "./components/DateRangeFields"
import TimeRangeFields from "./components/TimeRangeFields"
import ModalFooter from "./components/ModalFooter"
import styles from "./index.module.css"

dayjs.extend(customParseFormat)

const ScheduleTimeOffModal = ({
  open,
  onCancel,
  onSave,
  onSaveAndAddNew,
  editMode = false,
  initialValues,
  timeOffData = [],
  onDeleteTimeOff,
  onEditTimeOff,
  saveLoading = false,
  saveAndAddNewLoading = false
}) => {
  const { t } = useTranslation()

  // Form state and handlers
  const {
    form,
    isPartOfDay,
    setIsPartOfDay,
    showScheduledTimeOff,
    setShowScheduledTimeOff,
    isSaveEnabled,
    isSameDay,
    handleCancel,
    handleFinish,
    handleSaveAndAddNew,
    resetForm
  } = useScheduleTimeOffForm(onSave, onSaveAndAddNew, onCancel)

  // Update form when initialValues change
  useEffect(() => {
    if (open && initialValues) {
      // Set form fields
      form.setFieldsValue({
        startDate: initialValues.start_date ? dayjs(initialValues.start_date) : null,
        endDate: initialValues.end_date ? dayjs(initialValues.end_date) : null,
        // Parse time - backend returns "07:00 PM" format (uppercase AM/PM)
        startTime: initialValues.start_time ? dayjs(initialValues.start_time, ["hh:mm A", "hh:mm a"]) : null,
        endTime: initialValues.end_time ? dayjs(initialValues.end_time, ["hh:mm A", "hh:mm a"]) : null,
        reason: initialValues.reason,
        notes: initialValues.notes
      })

      // Set part of day checkbox - part_day: true means it's a part of day
      setIsPartOfDay(initialValues.part_day === true)
    } else if (open && !initialValues) {
      // Reset form for new entry
      form.resetFields()
      setIsPartOfDay(false)
    }
  }, [open, initialValues, form, setIsPartOfDay])

  // Edit/Delete handlers for table - delegate to parent
  const handleEdit = (record) => {
    if (onEditTimeOff) {
      onEditTimeOff(record)
    }
  }

  const handleDelete = (record) => {
    if (onDeleteTimeOff) {
      onDeleteTimeOff(record)
    }
  }

  // Table columns
  const columns = useScheduledTimeOffColumns(handleEdit, handleDelete)

  return (
      <Modal
        open={open}
        onCancel={handleCancel}
        title={editMode ? t("crewMember.scheduledTimeOff.editTitle") : t("crewMember.scheduledTimeOff.title")}
        width={800}
        className={styles.modal}
        centered
      >
        <Flex vertical gap={24} className="py-s">
          <Form form={form} onFinish={handleFinish} layout="vertical" initialValues={initialValues}>
            {/* Date Range Fields */}
            <DateRangeFields />

            {/* Part of Day Checkbox - Only show when start date equals end date */}
            {isSameDay && (
              <div className="py-xxs mb-xl">
                <Checkbox
                  checked={isPartOfDay}
                  onChange={(e) => setIsPartOfDay(e.target.checked)}
                  className={styles.partOfDayCheckbox}
                >
                  {t("crewMember.scheduledTimeOff.fields.partOfDay")}
                </Checkbox>
              </div>
            )}

            {/* Time Range Fields */}
            <TimeRangeFields isPartOfDay={isPartOfDay} />

            {/* Display Scheduled Time Off Button */}
            {/* <div className="mb-m">
              <Button
                type="text"
                className={styles.displayScheduledButton}
                onClick={() => setShowScheduledTimeOff(!showScheduledTimeOff)}
                icon={showScheduledTimeOff ? <UpOutlined /> : <DownOutlined />}
                iconPosition="end"
                color="primary"
                variant="outlined"
              >
                {showScheduledTimeOff
                  ? t("crewMember.scheduledTimeOff.buttons.hideScheduled")
                  : t("crewMember.scheduledTimeOff.buttons.displayScheduled")}
              </Button>
            </div> */}

            {/* Scheduled Time Off Table */}
            {showScheduledTimeOff && (
              <Flex vertical className={styles.scheduledTimeOffTable}>
                <Text size="lg" fw="600" className="mb-m">
                  {t("crewMember.scheduledTimeOff.table.title")}
                </Text>
                <DataTable columns={columns} dataSource={timeOffData} pagination={false} rowClickKey="id" />
              </Flex>
            )}
          </Form>

          {/* Modal Footer */}
          <ModalFooter
            editMode={editMode}
            isSaveEnabled={isSaveEnabled}
            onCancel={handleCancel}
            onSaveAndAddNew={handleSaveAndAddNew}
            onSubmit={() => form.submit()}
            saveLoading={saveLoading}
            saveAndAddNewLoading={saveAndAddNewLoading}
          />
        </Flex>
      </Modal>
  )
}

ScheduleTimeOffModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAndAddNew: PropTypes.func,
  editMode: PropTypes.bool,
  initialValues: PropTypes.object,
  timeOffData: PropTypes.array,
  onDeleteTimeOff: PropTypes.func,
  onEditTimeOff: PropTypes.func,
  saveLoading: PropTypes.bool,
  saveAndAddNewLoading: PropTypes.bool
}

export default ScheduleTimeOffModal
